<div class="page" [formGroup]="form">

  <!-- HEADER -->
  <header class="page__top">
    <h1 class="page__title">
      Novo cupom
      <span class="page__sub">— cadastro</span>
    </h1>
    <div class="page__actions">
      <cs-field-info-toggle></cs-field-info-toggle>
      <button type="button" class="btn btn--ghost" (click)="cancelar()">Cancelar</button>
    </div>
  </header>

  <!-- STEPPER -->
  <div class="stepper">
    @for (s of STEPS; track s.num) {
      <div
        class="step"
        [class.step--active]="isStepActive(s.num)"
        [class.step--done]="isStepDone(s.num)"
        (click)="goTo(s.num)">
        <div class="step__num">{{ s.num }}</div>
        <div class="step__lbl">
          {{ s.label }}
          @if (s.optional) { <span class="step__opt">opc</span> }
        </div>
      </div>
    }
  </div>


  <!-- ============================================================ -->
  <!-- PASSO 1 — BÁSICO                                              -->
  <!-- ============================================================ -->
  @if (currentStep() === 1) {
    <section class="panel">
      <h2 class="panel__title">Dados básicos do cupom</h2>
      <p class="panel__sub">Informações essenciais que identificam o cupom no sistema.</p>

      @if (erroPasso()) {
        <div class="alert alert--warn" style="margin-bottom: 16px">
          <span class="alert__icon">⚠</span>
          <div>{{ erroPasso() }}</div>
        </div>
      }

      <div class="row">
        <cs-form-field
          label="Código"
          fieldKey="cupom.codigo"
          [required]="true"
          hint="Maiúsculas, sem espaços. Identificador único.">
          <input type="text" formControlName="codigo" placeholder="Ex: CONSULTA69" />
        </cs-form-field>

        <cs-form-field
          label="Descrição"
          fieldKey="cupom.descricao"
          [required]="true">
          <input type="text" formControlName="descricao" placeholder="Texto que aparece para o operador" />
        </cs-form-field>
      </div>

      <div class="row row--full">
        <cs-form-field label="Observação interna" fieldKey="cupom.observacao">
          <textarea formControlName="observacao" placeholder="Notas para a equipe (não é exibida ao paciente)"></textarea>
        </cs-form-field>
      </div>

      <label class="check-row">
        <input type="checkbox" formControlName="emissaoManual" />
        <span class="check-row__text">
          <strong>Cupom de emissão manual</strong>
          <small>Só aparece na opção de conceder manualmente na Conferência por CPF (retenção/PEP). Não é emitido por gatilho automático.</small>
        </span>
      </label>

      <div class="section-title">Tipo do cupom</div>

      <div class="opt-grid">
        @for (opt of TIPO_OPTIONS; track opt.value) {
          <div
            class="opt-card"
            [class.opt-card--selected]="form.value.tipo === opt.value"
            (click)="selectTipo(opt.value)">
            <div class="opt-card__title">{{ opt.title }}</div>
            <div class="opt-card__desc">{{ opt.desc }}</div>
          </div>
        }
      </div>

      <div class="section-title">Tipo de desconto</div>

      <div class="opt-grid">
        @for (opt of DESCONTO_OPTIONS; track opt.value) {
          <div
            class="opt-card"
            [class.opt-card--selected]="form.value.tipoDesconto === opt.value"
            (click)="selectTipoDesconto(opt.value)">
            <div class="opt-card__title">{{ opt.title }}</div>
            <div class="opt-card__desc">{{ opt.desc }}</div>
          </div>
        }
      </div>

      <div class="row row--three mt-2">
        <cs-form-field label="Percentual" fieldKey="cupom.percentual" [required]="true" hint="Valor em %">
          <input type="number" formControlName="percentual" placeholder="10" />
        </cs-form-field>

        <cs-form-field label="Valor de desconto (R$)" fieldKey="cupom.valorDesconto" hint="Apenas para tipos Real/Preço fixo">
          <input type="number" formControlName="valorDesconto" placeholder="—" />
        </cs-form-field>

        <cs-form-field label="Teto de desconto (R$)" fieldKey="cupom.tetoDesconto" hint="Limite máx. de desconto aplicado">
          <input type="number" formControlName="tetoDesconto" placeholder="opcional" />
        </cs-form-field>
      </div>

      <div class="section-title">Vigência e limites de uso</div>

      <div class="row">
        <cs-form-field label="Início da vigência" fieldKey="cupom.dtInicio" [required]="true">
          <input type="date" formControlName="dtInicio" />
        </cs-form-field>

        <cs-form-field label="Fim da vigência" fieldKey="cupom.dtFim" hint="Em branco = sem prazo final">
          <input type="date" formControlName="dtFim" />
        </cs-form-field>
      </div>

      <div class="row row--three">
        <cs-form-field label="Total máx. de usos" fieldKey="cupom.qtMaxUsos" hint="Em todo o programa">
          <input type="number" formControlName="qtMaxUsos" placeholder="ilimitado" />
        </cs-form-field>

        <cs-form-field label="Por pessoa" fieldKey="cupom.qtPorPessoa" hint="Quantas vezes a mesma pessoa pode usar">
          <input type="number" formControlName="qtPorPessoa" />
        </cs-form-field>

        <cs-form-field label="Bloqueio entre usos (dias)" fieldKey="cupom.bloqueioDias" hint="Reuso só após X dias">
          <input type="number" formControlName="bloqueioDias" placeholder="0" />
        </cs-form-field>
      </div>

      <div class="wizard-nav">
        <div class="wizard-nav__info">Passo 1 de 6</div>
        <div class="wizard-nav__btns">
          <button type="button" class="btn" disabled>Anterior</button>
          <button type="button" class="btn btn--primary" (click)="goTo(2)">Próximo →</button>
        </div>
      </div>
    </section>
  }


  <!-- ============================================================ -->
  <!-- PASSO 2 — PROCEDIMENTOS                                       -->
  <!-- ============================================================ -->
  @if (currentStep() === 2) {
    <section class="panel">
      <h2 class="panel__title">Procedimentos elegíveis</h2>
      <p class="panel__sub">Defina em quais procedimentos o cupom pode ser aplicado.</p>

      <div class="alert alert--info">
        <span class="alert__icon">ⓘ</span>
        <div>
          Você pode adicionar procedimentos individuais (por código) ou regras por classificação
          (todos os procedimentos de uma categoria, ex: classif=13 = consultas).
        </div>
      </div>

      <div class="section-title">Por classificação <span class="muted">(recomendado)</span></div>

      @if (regrasClassif().length > 0) {
        <div class="table-wrap">
          <table>
            <thead>
              <tr>
                <th>Classificação</th>
                <th style="width: 80px">Modo</th>
                <th style="width: 60px"></th>
              </tr>
            </thead>
            <tbody>
              @for (r of regrasClassif(); track r.nrSeqClassif) {
                <tr>
                  <td>{{ r.dsClassificacao }}</td>
                  <td><span class="tag" [class.tag--green]="r.ieModo === 'I'" [class.tag--red]="r.ieModo === 'E'">{{ r.ieModo === 'I' ? 'Incluir' : 'Excluir' }}</span></td>
                  <td><button type="button" class="icon-btn" aria-label="Remover" (click)="removerClassificacao(r.nrSeqClassif)">×</button></td>
                </tr>
              }
            </tbody>
          </table>
        </div>
      } @else {
        <div class="empty">Nenhuma classificação adicionada.</div>
      }

      <div class="ac">
        <input type="text" placeholder="Digite para filtrar a classificação..."
               [ngModel]="classifBusca()" (ngModelChange)="classifBusca.set($event)" [ngModelOptions]="{standalone: true}" />
        @if (classifBusca().trim().length > 0) {
          @if (classifFiltradas().length > 0) {
            <ul class="ac-list">
              @for (c of classifFiltradas(); track c.nrSeqClassif) {
                <li (click)="selecionarClassif(c)">{{ c.dsClassificacao }}</li>
              }
            </ul>
          } @else {
            <ul class="ac-list"><li class="ac-empty">Nenhuma classificação encontrada.</li></ul>
          }
        }
      </div>

      <div class="section-title mt-2">Por procedimento individual <span class="muted">(opcional)</span></div>

      @if (regrasIndividuais().length > 0) {
        <div class="table-wrap">
          <table>
            <thead>
              <tr>
                <th style="width: 100px">Código</th>
                <th>Procedimento</th>
                <th style="width: 80px">Modo</th>
                <th style="width: 60px"></th>
              </tr>
            </thead>
            <tbody>
              @for (r of regrasIndividuais(); track r.cdProcedimento) {
                <tr>
                  <td><span class="tag">{{ r.cdProcedimento }}</span></td>
                  <td>{{ r.dsProcedimento }}</td>
                  <td><span class="tag" [class.tag--green]="r.ieModo === 'I'" [class.tag--red]="r.ieModo === 'E'">{{ r.ieModo === 'I' ? 'Incluir' : 'Excluir' }}</span></td>
                  <td><button type="button" class="icon-btn" aria-label="Remover" (click)="removerProcedimento(r.cdProcedimento)">×</button></td>
                </tr>
              }
            </tbody>
          </table>
        </div>
      } @else {
        <div class="empty">Nenhum procedimento individual adicionado.</div>
      }

      <div class="add-inline">
        <input type="text" placeholder="Buscar por nome do procedimento (digite ao menos 2 letras)..."
               [ngModel]="buscaProc()" (ngModelChange)="onBuscaProcChange($event)" [ngModelOptions]="{standalone: true}" />
        @if (buscando()) { <span class="ac-loading">Buscando…</span> }
      </div>

      @if (resultadosBusca().length > 0) {
        <div class="table-wrap" style="margin-top: 8px; max-height: 240px; overflow-y: auto;">
          <table>
            <thead>
              <tr>
                <th style="width: 100px">Código</th>
                <th>Procedimento</th>
                <th style="width: 60px"></th>
              </tr>
            </thead>
            <tbody>
              @for (p of resultadosBusca(); track p.cdProcedimento) {
                <tr>
                  <td>{{ p.cdProcedimento }}</td>
                  <td>{{ p.dsProcedimento }}</td>
                  <td><button type="button" class="btn" style="padding: 2px 8px; font-size: 12px;" (click)="adicionarProcedimento(p)">+</button></td>
                </tr>
              }
            </tbody>
          </table>
        </div>
      }

      @if (buscando()) {
        <div class="muted" style="margin-top: 8px;">Buscando...</div>
      }

      <div class="wizard-nav">
        <div class="wizard-nav__info">Passo 2 de 6</div>
        <div class="wizard-nav__btns">
          <button type="button" class="btn" (click)="goTo(1)">← Anterior</button>
          <button type="button" class="btn btn--primary" (click)="goTo(3)">Próximo →</button>
        </div>
      </div>
    </section>
  }


  <!-- ============================================================ -->
  <!-- PASSO 3 — MATRIZ DE VALOR (opcional)                           -->
  <!-- ============================================================ -->
  @if (currentStep() === 3) {
    <section class="panel">
      <h2 class="panel__title">Matriz de valor <span class="muted">— opcional</span></h2>
      <p class="panel__sub">
        Configure faixas de desconto diferenciadas. Cada faixa pode ter percentual ou valor fixo.
        Se nao configurar, o cupom usa o desconto do passo 1.
      </p>

      <div class="section-title">Faixas de desconto</div>

      @if (faixas().length > 0) {
        <div class="table-wrap">
          <table>
            <thead>
              <tr>
                <th style="width: 50px">#</th>
                <th>Descricao</th>
                <th style="width: 120px">Desconto</th>
                <th style="width: 80px">Teto</th>
                <th style="width: 60px"></th>
              </tr>
            </thead>
            <tbody>
              @for (f of faixas(); track f.nrOrdem) {
                <tr>
                  <td>{{ f.nrOrdem }}</td>
                  <td>{{ f.dsRegra }}</td>
                  <td>
                    @if (f.prDesconto != null) {
                      <span class="tag tag--green">{{ f.prDesconto }}%</span>
                    } @else {
                      <span class="tag">R$ {{ f.vlFixo }}</span>
                    }
                  </td>
                  <td>{{ f.vlTeto != null ? 'R$ ' + f.vlTeto : '—' }}</td>
                  <td><button type="button" class="icon-btn" aria-label="Remover" (click)="removerFaixa(f.nrOrdem)">x</button></td>
                </tr>
              }
            </tbody>
          </table>
        </div>
      } @else {
        <div class="empty">Nenhuma faixa configurada. O cupom usara o desconto do passo 1.</div>
      }

      <div class="add-inline" style="flex-wrap: wrap; gap: 8px;">
        <input type="text" placeholder="Descricao da faixa" #faixaDesc style="flex: 2; min-width: 180px;" />
        <input type="number" placeholder="% desconto" #faixaPr style="width: 100px;" />
        <input type="number" placeholder="R$ fixo" #faixaVl style="width: 100px;" />
        <input type="number" placeholder="Teto R$" #faixaTeto style="width: 90px;" />
        <button type="button" class="btn" (click)="adicionarFaixa(faixaDesc.value, faixaPr.value ? +faixaPr.value : null, faixaVl.value ? +faixaVl.value : null, faixaTeto.value ? +faixaTeto.value : null, 'N'); faixaDesc.value=''; faixaPr.value=''; faixaVl.value=''; faixaTeto.value=''">+ Adicionar</button>
      </div>

      <div class="wizard-nav">
        <div class="wizard-nav__info">Passo 3 de 6 — opcional</div>
        <div class="wizard-nav__btns">
          <button type="button" class="btn" (click)="goTo(2)">← Anterior</button>
          <button type="button" class="btn" (click)="goTo(4)">Pular</button>
          <button type="button" class="btn btn--primary" (click)="goTo(4)">Proximo →</button>
        </div>
      </div>
    </section>
  }


  <!-- ============================================================ -->
  <!-- PASSO 4 — FILTROS (opcional)                                  -->
  <!-- ============================================================ -->
  @if (currentStep() === 4) {
    <section class="panel">
      <h2 class="panel__title">Filtros adicionais <span class="muted">— opcional</span></h2>
      <p class="panel__sub">
        Restrinja o cupom a perfis específicos. Filtros desligados aceitam qualquer caso.
      </p>

      <div class="filter-detail" style="margin-bottom: 16px">
        <cs-form-field label="Beneficiário na assinatura" fieldKey="cupom.beneficiario">
          <select formControlName="beneficiario">
            <option value="T">Titular — só o assinante titular</option>
            <option value="D">Dependente — só os dependentes ativos</option>
            <option value="C">Compartilhado — cota única para qualquer membro</option>
            <option value="I">Individual — um cupom para cada pessoa (titular + dependentes)</option>
          </select>
        </cs-form-field>
        <p class="muted" style="margin-top: 6px; font-size: 12px">
          Define para quem o cupom é emitido numa assinatura CartãoSIM (B2C) ou Empresa (B2B).
          A emissão por papel (titular/dependente) ainda será ligada na procedure de emissão.
        </p>
      </div>

      <div class="toggle-row">
        <div class="toggle-row__info">
          <div class="toggle-row__title">Restringir por plano B2C</div>
          <div class="toggle-row__desc">Cupom só aplicável a clientes com determinados planos CartãoSIM.</div>
        </div>
        <div class="switch" [class.switch--on]="isSwitchOn('plano')" (click)="toggleSwitchFiltro('plano')"></div>
      </div>
      @if (isSwitchOn('plano')) {
        <div class="filter-detail">
          @if (regrasPlano().length > 0) {
            <div class="table-wrap">
              <table>
                <thead><tr><th>Plano</th><th style="width:80px">Modo</th><th style="width:60px"></th></tr></thead>
                <tbody>
                  @for (r of regrasPlano(); track r.cdPlan) {
                    <tr>
                      <td>{{ r.dsPlano }}</td>
                      <td><span class="tag" [class.tag--green]="r.ieModo === 'I'" [class.tag--red]="r.ieModo === 'E'">{{ r.ieModo === 'I' ? 'Incluir' : 'Excluir' }}</span></td>
                      <td><button type="button" class="icon-btn" (click)="removerPlano(r.cdPlan)">×</button></td>
                    </tr>
                  }
                </tbody>
              </table>
            </div>
          }
          <div class="add-inline" style="margin-top: 0">
            <select [ngModel]="planoSelecionado()" (ngModelChange)="planoSelecionado.set($event)" [ngModelOptions]="{standalone: true}">
              <option [ngValue]="null">Selecionar plano...</option>
              @for (p of planosDisponiveis; track p.cdPlano) {
                <option [ngValue]="p.cdPlano">{{ p.dsPlano }}</option>
              }
            </select>
            <button type="button" class="btn" (click)="adicionarPlano()" [disabled]="planoSelecionado() == null">+ Adicionar</button>
          </div>
        </div>
      }

      <div class="toggle-row">
        <div class="toggle-row__info">
          <div class="toggle-row__title">Restringir por empresa B2B</div>
          <div class="toggle-row__desc">Cupom só aplicável a colaboradores de determinadas empresas parceiras.</div>
        </div>
        <div class="switch" [class.switch--on]="isSwitchOn('emp')" (click)="toggleSwitchFiltro('emp')"></div>
      </div>
      @if (isSwitchOn('emp')) {
        <div class="filter-detail">
          @if (regrasEmpresa().length > 0) {
            <div class="table-wrap">
              <table>
                <thead><tr><th>Empresa</th><th style="width:80px">Modo</th><th style="width:60px"></th></tr></thead>
                <tbody>
                  @for (r of regrasEmpresa(); track r.cdEmpresa) {
                    <tr>
                      <td>{{ r.dsEmpresa }}</td>
                      <td><span class="tag" [class.tag--green]="r.ieModo === 'I'" [class.tag--red]="r.ieModo === 'E'">{{ r.ieModo === 'I' ? 'Incluir' : 'Excluir' }}</span></td>
                      <td><button type="button" class="icon-btn" (click)="removerEmpresa(r.cdEmpresa)">×</button></td>
                    </tr>
                  }
                </tbody>
              </table>
            </div>
          }
          <div class="add-inline" style="margin-top: 0">
            <select [ngModel]="empresaSelecionada()" (ngModelChange)="empresaSelecionada.set($event)" [ngModelOptions]="{standalone: true}">
              <option [ngValue]="null">Selecionar empresa...</option>
              @for (e of empresasDisponiveis; track e.cdEmpresa) {
                <option [ngValue]="e.cdEmpresa">{{ e.dsEmpresa }}</option>
              }
            </select>
            <button type="button" class="btn" (click)="adicionarEmpresa()" [disabled]="empresaSelecionada() == null">+ Adicionar</button>
          </div>
        </div>
      }

      <div class="toggle-row">
        <div class="toggle-row__info">
          <div class="toggle-row__title">Restringir por estabelecimento</div>
          <div class="toggle-row__desc">Cupom só aplicável em determinadas unidades.</div>
        </div>
        <div class="switch" [class.switch--on]="isSwitchOn('estab')" (click)="toggleSwitchFiltro('estab')"></div>
      </div>
      @if (isSwitchOn('estab')) {
        <div class="filter-detail">
          @if (regrasEstabelecimento().length > 0) {
            <div class="table-wrap">
              <table>
                <thead><tr><th>Estabelecimento</th><th style="width:80px">Modo</th><th style="width:60px"></th></tr></thead>
                <tbody>
                  @for (r of regrasEstabelecimento(); track r.cdEstabelecimento) {
                    <tr>
                      <td>{{ r.dsEstabelecimento }}</td>
                      <td><span class="tag" [class.tag--green]="r.ieModo === 'I'" [class.tag--red]="r.ieModo === 'E'">{{ r.ieModo === 'I' ? 'Incluir' : 'Excluir' }}</span></td>
                      <td><button type="button" class="icon-btn" (click)="removerEstabelecimento(r.cdEstabelecimento)">×</button></td>
                    </tr>
                  }
                </tbody>
              </table>
            </div>
          }
          <div class="add-inline" style="margin-top: 0">
            <select [ngModel]="estabSelecionado()" (ngModelChange)="estabSelecionado.set($event)" [ngModelOptions]="{standalone: true}">
              <option [ngValue]="null">Selecionar estabelecimento...</option>
              @for (e of estabDisponiveis; track e.cdEstabelecimento) {
                <option [ngValue]="e.cdEstabelecimento">{{ e.dsEstabelecimento }}</option>
              }
            </select>
            <button type="button" class="btn" (click)="adicionarEstabelecimento()" [disabled]="estabSelecionado() == null">+ Adicionar</button>
          </div>
        </div>
      }

      <div class="toggle-row">
        <div class="toggle-row__info">
          <div class="toggle-row__title">Restringir por profissional</div>
          <div class="toggle-row__desc">Cupom só aplicável em consultas/exames com determinados profissionais.</div>
        </div>
        <div class="switch" [class.switch--on]="isSwitchOn('prof')" (click)="toggleSwitch('prof')"></div>
      </div>

      <div class="toggle-row">
        <div class="toggle-row__info">
          <div class="toggle-row__title">Restringir por faixa etária</div>
          <div class="toggle-row__desc">Cupom só aplicável a pacientes em determinada faixa de idade.</div>
        </div>
        <div class="switch" [class.switch--on]="isSwitchOn('idade')" (click)="toggleSwitch('idade')"></div>
      </div>
      @if (isSwitchOn('idade')) {
        <div class="filter-detail">
          <div class="row">
            <cs-form-field label="Idade mínima" fieldKey="cupom.regra.idadeMin">
              <input type="number" formControlName="idadeMin" placeholder="0" />
            </cs-form-field>
            <cs-form-field label="Idade máxima" fieldKey="cupom.regra.idadeMax">
              <input type="number" formControlName="idadeMax" placeholder="120" />
            </cs-form-field>
          </div>
        </div>
      }

      <div class="wizard-nav">
        <div class="wizard-nav__info">Passo 4 de 6 — opcional</div>
        <div class="wizard-nav__btns">
          <button type="button" class="btn" (click)="goTo(3)">← Anterior</button>
          <button type="button" class="btn" (click)="goTo(5)">Pular</button>
          <button type="button" class="btn btn--primary" (click)="goTo(5)">Próximo →</button>
        </div>
      </div>
    </section>
  }


  <!-- ============================================================ -->
  <!-- PASSO 5 — COMBO & DEPENDÊNCIA (opcional)                      -->
  <!-- ============================================================ -->
  @if (currentStep() === 5) {
    <section class="panel">
      <h2 class="panel__title">Combo e dependências <span class="muted">— opcional</span></h2>
      <p class="panel__sub">
        Configure relacionamentos entre este cupom e outros (combos pai/filho ou pré-requisitos).
      </p>

      <div class="section-title">Combo (cupons emitidos junto)</div>

      <div class="alert alert--info">
        <span class="alert__icon">ⓘ</span>
        <div>
          Se este cupom for um <strong>cupom-mãe</strong>, ao ser emitido para um paciente
          ele dispara automaticamente a emissão de seus cupons-filhos.
        </div>
      </div>

      <div class="toggle-row toggle-row--bordered">
        <div class="toggle-row__info">
          <div class="toggle-row__title">Este cupom é cupom-mãe (emite outros junto)</div>
          <div class="toggle-row__desc">Ative para configurar cupons-filhos.</div>
        </div>
        <div class="switch" [class.switch--on]="isSwitchOn('pai')" (click)="toggleSwitch('pai')"></div>
      </div>
      @if (isSwitchOn('pai')) {
        <div class="filter-detail">
          @if (combos().length > 0) {
            <div class="table-wrap">
              <table>
                <thead><tr><th style="width:50px">#</th><th>Cupom filho</th><th style="width:120px">Offset (dias)<span class="info-tip" tabindex="0">i<span class="info-tip__bubble">Dias de carência até o cupom-filho ficar disponível para uso, contados a partir da emissão do cupom-mãe. 0 = liberado na hora.</span></span></th><th style="width:60px"></th></tr></thead>
                <tbody>
                  @for (c of combos(); track c.nrSeqCupomFilho) {
                    <tr>
                      <td>{{ c.nrOrdem }}</td>
                      <td><span class="tag">{{ c.codCupom }}</span></td>
                      <td><input type="number" min="0" class="offset-input" [ngModel]="c.nrDiasOffsetLiberacao" (ngModelChange)="setComboOffset(c.nrSeqCupomFilho, $event)" [ngModelOptions]="{standalone: true}" /> <span class="offset-un">d</span></td>
                      <td><button type="button" class="icon-btn" (click)="removerCombo(c.nrSeqCupomFilho)">×</button></td>
                    </tr>
                  }
                </tbody>
              </table>
            </div>
          } @else {
            <div class="empty">Nenhum cupom-filho adicionado.</div>
          }
          <div class="add-inline" style="margin-top: 8px">
            <input type="text" placeholder="Buscar cupom filho por código..." [ngModel]="buscaCupomCombo()" (ngModelChange)="buscaCupomCombo.set($event)" [ngModelOptions]="{standalone: true}" (keyup.enter)="buscarCupons('combo')" />
            <button type="button" class="btn" (click)="buscarCupons('combo')" [disabled]="buscaCupomCombo().length < 2">Buscar</button>
          </div>
          @if (resultadosBuscaCupom().length > 0 && buscaCupomCombo().length > 0) {
            <div class="table-wrap" style="margin-top: 8px; max-height: 200px; overflow-y: auto;">
              <table>
                <thead><tr><th>Código</th><th>Descrição</th><th style="width:60px"></th></tr></thead>
                <tbody>
                  @for (r of resultadosBuscaCupom(); track r.nrSequencia) {
                    <tr>
                      <td><span class="tag">{{ r.codCupom }}</span></td>
                      <td>{{ r.dsCupom }}</td>
                      <td><button type="button" class="btn" style="padding:2px 8px;font-size:12px" (click)="adicionarCombo(r)">+</button></td>
                    </tr>
                  }
                </tbody>
              </table>
            </div>
          }
          @if (buscandoCupom()) {
            <div class="muted" style="margin-top: 8px;">Buscando...</div>
          }
        </div>
      }

      <div class="section-title mt-2">Dependência (cupom exigido antes)</div>

      <div class="toggle-row toggle-row--bordered">
        <div class="toggle-row__info">
          <div class="toggle-row__title">Este cupom exige outro usado antes</div>
          <div class="toggle-row__desc">Ex: RETCONSULTA69 só pode ser usado se PRIMEIRACONSULTA69 já foi.</div>
        </div>
        <div class="switch" [class.switch--on]="isSwitchOn('dep')" (click)="toggleSwitch('dep')"></div>
      </div>
      @if (isSwitchOn('dep')) {
        <div class="filter-detail">
          @if (dependencias().length > 0) {
            <div class="table-wrap">
              <table>
                <thead><tr><th>Cupom exigido</th><th style="width:100px">Tipo</th><th style="width:60px"></th></tr></thead>
                <tbody>
                  @for (d of dependencias(); track d.nrSeqCupomDep) {
                    <tr>
                      <td><span class="tag">{{ d.codCupom }}</span></td>
                      <td>{{ d.ieTipoDep === 'U' ? 'Utilização' : 'Emissão' }}</td>
                      <td><button type="button" class="icon-btn" (click)="removerDependencia(d.nrSeqCupomDep)">×</button></td>
                    </tr>
                  }
                </tbody>
              </table>
            </div>
          }
          <div class="add-inline" style="margin-top: 8px">
            <input type="text" placeholder="Buscar cupom pré-requisito..." [ngModel]="buscaCupomDep()" (ngModelChange)="buscaCupomDep.set($event)" [ngModelOptions]="{standalone: true}" (keyup.enter)="buscarCupons('dep')" />
            <button type="button" class="btn" (click)="buscarCupons('dep')" [disabled]="buscaCupomDep().length < 2">Buscar</button>
          </div>
          @if (resultadosBuscaCupom().length > 0 && buscaCupomDep().length > 0) {
            <div class="table-wrap" style="margin-top: 8px; max-height: 200px; overflow-y: auto;">
              <table>
                <thead><tr><th>Código</th><th>Descrição</th><th style="width:60px"></th></tr></thead>
                <tbody>
                  @for (r of resultadosBuscaCupom(); track r.nrSequencia) {
                    <tr>
                      <td><span class="tag">{{ r.codCupom }}</span></td>
                      <td>{{ r.dsCupom }}</td>
                      <td><button type="button" class="btn" style="padding:2px 8px;font-size:12px" (click)="adicionarDependencia(r)">+</button></td>
                    </tr>
                  }
                </tbody>
              </table>
            </div>
          }
          @if (buscandoCupom()) {
            <div class="muted" style="margin-top: 8px;">Buscando...</div>
          }
        </div>
      }

      <div class="wizard-nav">
        <div class="wizard-nav__info">Passo 5 de 6 — opcional</div>
        <div class="wizard-nav__btns">
          <button type="button" class="btn" (click)="goTo(4)">← Anterior</button>
          <button type="button" class="btn" (click)="goTo(6)">Pular</button>
          <button type="button" class="btn btn--primary" (click)="goTo(6)">Próximo →</button>
        </div>
      </div>
    </section>
  }


  <!-- ============================================================ -->
  <!-- PASSO 6 — REVISÃO                                             -->
  <!-- ============================================================ -->
  @if (currentStep() === 6) {
    <section class="panel">
      <h2 class="panel__title">Revisão</h2>
      <p class="panel__sub">Confira tudo antes de criar o cupom.</p>

      <div class="summary-grid">

        <div class="summary-card">
          <div class="summary-card__head">
            <span class="summary-card__title">Dados básicos</span>
            <button type="button" class="summary-card__edit" (click)="goTo(1)">Editar</button>
          </div>
          <dl class="summary-card__dl">
            <dt>Código</dt><dd><strong>{{ codigo }}</strong></dd>
            <dt>Descrição</dt><dd>{{ descricao }}</dd>
            <dt>Tipo</dt><dd>{{ tipoLabel }}</dd>
            <dt>Tipo de desconto</dt><dd>{{ tipoDescontoLabel }}</dd>
            <dt>Vigência</dt><dd>{{ vigenciaLabel }}</dd>
            <dt>Limites</dt><dd>{{ limitesLabel }}</dd>
          </dl>
        </div>

        <div class="summary-card">
          <div class="summary-card__head">
            <span class="summary-card__title">Procedimentos elegíveis</span>
            <button type="button" class="summary-card__edit" (click)="goTo(2)">Editar</button>
          </div>
          <dl class="summary-card__dl">
            <dt>Regras</dt><dd>{{ resumoProcsLabel }}</dd>
          </dl>
        </div>

        <div class="summary-card">
          <div class="summary-card__head">
            <span class="summary-card__title">Matriz de valor</span>
            <button type="button" class="summary-card__edit" (click)="goTo(3)">Editar</button>
          </div>
          <dl class="summary-card__dl">
            <dt>Faixas</dt><dd>{{ resumoMatrizLabel }}</dd>
          </dl>
        </div>

        @if (resumoFiltrosLabel) {
          <div class="summary-card">
            <div class="summary-card__head">
              <span class="summary-card__title">Filtros</span>
              <button type="button" class="summary-card__edit" (click)="goTo(4)">Editar</button>
            </div>
            <dl class="summary-card__dl">
              <dt>Regras</dt><dd>{{ resumoFiltrosLabel }}</dd>
            </dl>
          </div>
        } @else {
          <div class="summary-card summary-card--empty">
            Sem filtros adicionais (cupom aplicável a qualquer paciente, plano, empresa, etc).
            <button type="button" class="summary-card__edit" (click)="goTo(4)" style="margin-left: 8px">Configurar</button>
          </div>
        }

        @if (resumoComboLabel) {
          <div class="summary-card">
            <div class="summary-card__head">
              <span class="summary-card__title">Combo e dependências</span>
              <button type="button" class="summary-card__edit" (click)="goTo(5)">Editar</button>
            </div>
            <dl class="summary-card__dl">
              <dt>Regras</dt><dd>{{ resumoComboLabel }}</dd>
            </dl>
          </div>
        } @else {
          <div class="summary-card summary-card--empty">
            Sem combos ou dependências.
            <button type="button" class="summary-card__edit" (click)="goTo(5)" style="margin-left: 8px">Configurar</button>
          </div>
        }

      </div>

      <div class="alert alert--info" style="margin-top: 24px">
        <span class="alert__icon">ⓘ</span>
        <div>
          Após criar o cupom, ele estará disponível para emissão. Lembre-se de cadastrar
          o gatilho de emissão em <strong>Gatilhos &gt; Configuração</strong> se desejar
          emitir automaticamente.
        </div>
      </div>

      @if (erroSalvar()) {
        <div class="alert alert--warn" style="margin-top: 16px">
          <span class="alert__icon">⚠</span>
          <div>{{ erroSalvar() }}</div>
        </div>
      }

      <div class="wizard-nav">
        <div class="wizard-nav__info">{{ salvando() ? 'Criando cupom...' : 'Pronto para criar' }}</div>
        <div class="wizard-nav__btns">
          <button type="button" class="btn" (click)="goTo(5)" [disabled]="salvando()">← Anterior</button>
          <button type="button" class="btn btn--primary" (click)="salvar()" [disabled]="salvando()">
            {{ salvando() ? 'Criando...' : 'Criar cupom' }}
          </button>
        </div>
      </div>
    </section>
  }

</div>
